'use client';
import React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { motion } from 'motion/react';
import { cn } from '@/lib/utils';

interface AnimatedBorderProps {
  children: React.ReactNode;
  className?: string;
  borderColor?: string;
  hoverBorderColor?: string;
  borderWidth?: number;
  borderRadius?: string;
  background?: string;
  hoverBackground?: string;
  speed?: number;
  hoverSpeed?: number;
  spread?: number;
  cut?: number;
  showAnimation?: boolean;
  asChild?: boolean;
  animated?: boolean;
  creativeMode?: 'pulse' | 'wave' | 'spiral' | 'gradient';
}

const AnimatedBorder = ({
  children,
  className,
  borderColor = '#8752FA',
  hoverBorderColor = '#A855F7',
  borderWidth = 2,
  borderRadius = 'rounded-lg',
  background = 'transparent',
  hoverBackground = 'rgba(135, 82, 250, 0.05)',
  speed = 3,
  hoverSpeed = 1.5,
  spread = 90,
  cut = 2,
  showAnimation = true,
  asChild = false,
  animated = true,
  creativeMode = 'gradient',
}: AnimatedBorderProps) => {
  const Comp = asChild ? Slot : 'div';

  const getCreativeAnimation = () => {
    switch (creativeMode) {
      case 'pulse':
        return 'animate-pulse';
      case 'wave':
        return 'animate-wave';
      case 'spiral':
        return 'animate-spiral';
      case 'gradient':
      default:
        return '';
    }
  };

  return (
    <Comp
      className={cn(
        'group relative overflow-hidden transition-all duration-300',
        borderRadius,
        getCreativeAnimation(),
        className
      )}
      style={{
        '--border-color': borderColor,
        '--hover-border-color': hoverBorderColor,
        '--border-width': `${borderWidth}px`,
        '--speed': `${speed}s`,
        '--hover-speed': `${hoverSpeed}s`,
        '--spread': `${spread}deg`,
        '--cut': `${cut}px`,
        '--bg': background,
        '--hover-bg': hoverBackground,
      } as React.CSSProperties}
    >
      {/* Rotating Border Span - Only show if animation is enabled */}
      {showAnimation && animated && (
        <span className="absolute inset-0 overflow-hidden [border-radius:inherit]">
          {/* GRADIENT */}
          <motion.span 
            className="absolute inset-[-100%] rotate-gradient"
            animate={{ rotate: 360 }}
            transition={{ duration: speed, repeat: Infinity, ease: 'linear' }}
          >
            <span 
              className="absolute inset-0"
              style={{
                background: `conic-gradient(from calc(270deg - (var(--spread) * 0.5)), transparent 0, var(--border-color) var(--spread), transparent var(--spread))`,
                transition: `background var(--hover-speed) ease-in-out`
              }}
            />
          </motion.span>
          {/* END GRADIENT */}
        </span>
      )}

      {/* Static Border - Show when not animated */}
      {showAnimation && !animated && (
        <span className="absolute inset-0 overflow-hidden [border-radius:inherit]">
          <span 
            className="absolute inset-0"
            style={{
              background: `conic-gradient(from calc(270deg - (var(--spread) * 0.5)), transparent 0, var(--border-color) var(--spread), transparent var(--spread))`,
            }}
          />
        </span>
      )}

      {/* Background Cut Layer */}
      <span className="absolute [background:var(--bg)] [border-radius:inherit] [inset:var(--cut)] group-hover:[background:var(--hover-bg)]" />

      {/* Content */}
      <span className="relative z-10 block">
        {children}
      </span>
    </Comp>
  );
};

export default AnimatedBorder; 
